import { Flex, Group, Stack } from '@mantine/core';
import type {
	EntityViolationsOut,
	PolicyOut,
	PolicyViolationOut,
} from '@repo/api-codegen';
import { Text } from '@repo/foundations';
import { observer } from 'mobx-react-lite';
import { useCallback } from 'react';
import { formatDateTimeWithAt } from '../../../../../utils/time';
import { PiiClassifier } from '../PiiClassifier';
import { PolicyDataSamples } from '../PolicyDataSamples';
import { ViolationStatusBadge } from '../ViolationStatusBadge';
import policyIssueStore from '../store';
import { useStyles } from '../styles';

export const PolicyColumnContent = observer(
	({
		columns,
		policy,
	}: {
		columns: EntityViolationsOut[];
		policy: PolicyOut;
	}) => {
		const { classes, theme } = useStyles();
		const handleColumnClick = useCallback(
			(id: string, violations: PolicyViolationOut[]) => {
				policyIssueStore.openPolicyIssueSidebarWithId(policy, violations, id);
			},
			[policy]
		);

		return (
			<Stack w="100%" spacing={0}>
				{columns.map((column) => (
					<Group
						key={column.entity.id}
						className={classes.rowContainer}
						p="xs"
						onClick={() =>
							handleColumnClick(column.entity.id, column.violations)
						}
					>
						<Flex
							className={classes.columnColumn}
							p={`${theme.spacing.sm} ${theme.spacing.md} ${theme.spacing.sm} ${theme.spacing.lg}`}
						>
							<Text size="sm">{column.entity.title}</Text>
						</Flex>
						<Flex
							className={classes.piiColumn}
							p={`${theme.spacing.sm} ${theme.spacing.md} ${theme.spacing.sm} ${theme.spacing.xs}`}
						>
							<PiiClassifier violations={column.violations} />
						</Flex>
						<Flex
							className={classes.sampleDataColumn}
							p={`${theme.spacing.sm} ${theme.spacing.md} ${theme.spacing.sm} ${theme.spacing.xs}`}
						>
							{column.violations.map((violation) => (
								<PolicyDataSamples
									key={violation.id}
									dataSamples={violation.data_samples ?? []}
									displayLimit={0}
								/>
							))}
						</Flex>
						<Flex
							className={classes.statusColumn}
							p={`${theme.spacing.sm} ${theme.spacing.md} ${theme.spacing.sm} ${theme.spacing.xs}`}
						>
							<ViolationStatusBadge violations={column.violations} />
						</Flex>
						<Flex
							className={classes.dateColumn}
							p={`${theme.spacing.sm} ${theme.spacing.md} ${theme.spacing.sm} ${theme.spacing.xs}`}
						>
							<Text size="sm" truncate>
								{formatDateTimeWithAt(
									column.violations?.[0]?.created_at ?? null
								)}
							</Text>
						</Flex>
					</Group>
				))}
			</Stack>
		);
	}
);
